import {
    GET_APPLIED_CASHPASS,
    GET_APPLIED_CASHPASS_SUCCESS,
    GET_APPLIED_CASHPASS_ERROR,
    APPLY_CASHPASS,
    APPLY_CASHPASS_SUCCESS,
    APPLY_CASHPASS_ERROR,
    REMOVE_CASHPASS,
    REMOVE_CASHPASS_SUCCESS,
    REMOVE_CASHPASS_ERROR,
    CLEAR_CASHPASS_ERROR,
    SET_CASHPASS_LOADING,
} from '../actionTypes/CashpassCartActionTypes';

/**
 * State shape:
 * {
 *   bySerialNumber: {
 *     [serialNumber]: { id, applied }   // id = adjustment id used for DELETE
 *   },
 *   loading: boolean,                    // an apply/remove/get is in flight
 *   error: object|null,                  // last raw error
 *   errorMessage: string|null,           // last errorMessage from response (for future UI)
 *   errorCode: string|null,              // last errorCode from response
 *   errorSerialNumber: string|null,      // which card the error belongs to
 * }
 */
export const initialState = {
    bySerialNumber: {},
    loading: false,
    error: null,
    errorMessage: null,
    errorCode: null,
    errorSerialNumber: null,
};

/**
 * Builds the bySerialNumber map from the applied-rewards list. Each applied
 * REWARDSCERT adjustment carries the `id` (delete key) and `serialNumber`.
 * @param {Array<Object>} appliedRewards
 * @returns {Object}
 */
const buildAppliedMap = (appliedRewards = []) => {
    const bySerialNumber = {};
    appliedRewards.forEach((reward) => {
        if (reward && reward.serialNumber) {
            bySerialNumber[reward.serialNumber] = {
                id: reward.id,
                applied: true,
            };
        }
    });
    return bySerialNumber;
};

export default function CashpassCartReducer(state = initialState, action = {}) {
    switch (action.type) {
        case SET_CASHPASS_LOADING:
            // Loader lifecycle is owned by the saga (on before the API call,
            // off once results are out).
            return {
                ...state,
                loading: Boolean(action.payload && action.payload.loading),
            };

        case GET_APPLIED_CASHPASS:
        case APPLY_CASHPASS:
        case REMOVE_CASHPASS:
            // Clear any previous error when a new request starts. Loading is
            // toggled separately via SET_CASHPASS_LOADING (saga-driven).
            return {
                ...state,
                error: null,
                errorMessage: null,
                errorCode: null,
                errorSerialNumber: null,
            };

        case GET_APPLIED_CASHPASS_SUCCESS:
            return {
                ...state,
                loading: false,
                bySerialNumber: buildAppliedMap(action.appliedRewards),
            };

        case APPLY_CASHPASS_SUCCESS:
            // Mark applied optimistically; the follow-up GET fills in the real id.
            return {
                ...state,
                loading: false,
                bySerialNumber: {
                    ...state.bySerialNumber,
                    [action.serialNumber]: {
                        ...state.bySerialNumber[action.serialNumber],
                        applied: true,
                    },
                },
            };

        case REMOVE_CASHPASS_SUCCESS: {
            const bySerialNumber = { ...state.bySerialNumber };
            if (action.serialNumber) {
                delete bySerialNumber[action.serialNumber];
            }
            return {
                ...state,
                loading: false,
                bySerialNumber,
            };
        }

        case GET_APPLIED_CASHPASS_ERROR:
        case APPLY_CASHPASS_ERROR:
        case REMOVE_CASHPASS_ERROR:
            // errorMessage retained for future UI (design in progress).
            return {
                ...state,
                loading: false,
                error: action.error || null,
                errorMessage: action.errorMessage || null,
                errorCode: action.errorCode || null,
                errorSerialNumber: action.serialNumber || null,
            };

        case CLEAR_CASHPASS_ERROR: {
            const clearSerial = action.payload && action.payload.serialNumber;
            if (clearSerial && clearSerial !== state.errorSerialNumber) {
                return state;
            }
            return {
                ...state,
                error: null,
                errorMessage: null,
                errorCode: null,
                errorSerialNumber: null,
            };
        }

        default:
            return state;
    }
}

CashpassCartReducer.reducerName = 'cashpassCart';
